import { useEffect } from 'react';
import { AccountLayout } from '@/components/AccountLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate, Link } from 'react-router-dom';
import { requireUser } from '@/lib/requireUser';
import { Sparkles, Shield, Percent, Crown } from 'lucide-react';
import { membershipService } from '@/data/membership';

const ProMembership = () => {
  const { user, isProMember, membership } = useAuth();
  const navigate = useNavigate();
  const isFounder = membershipService.isFounder(membership);

  useEffect(() => {
    requireUser({ user, navigate, currentPath: '/account/pro' });
  }, [user, navigate]);

  if (!user) return null;

  const currentTier = membership?.tier || 'free';

  return (
    <AccountLayout>
      <div className="space-y-6">
        {/* Hero Section */}
        <div className="text-center space-y-4 py-8">
          <Badge variant="secondary" className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
            <Sparkles className="h-3 w-3 mr-1" />
            Premium Membership
          </Badge>
          <h1 className="text-4xl font-bold tracking-tight">Say hello to Circle Pro</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Unlock exclusive benefits, quarterly credits, and save 10% on every purchase
          </p>
        </div>

        {/* Current Status */}
        <Card className="border-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Current Membership</CardTitle>
                <CardDescription>Your active subscription plan</CardDescription>
              </div>
              <Badge 
                variant={isFounder ? "default" : isProMember ? "default" : "secondary"}
                className={isFounder ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white" : ""}
              >
                {isFounder ? (
                  <>
                    <Crown className="h-3 w-3 mr-1" />
                    Founder
                  </>
                ) : isProMember ? (
                  'Pro'
                ) : (
                  'Free'
                )}
              </Badge>
            </div>
          </CardHeader>
          {!isProMember && !isFounder && (
            <CardContent>
              <Link to="/pricing">
                <Button size="lg" className="w-full">
                  Upgrade to Pro
                </Button>
              </Link>
            </CardContent>
          )}
        </Card>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <Shield className="h-12 w-12 text-blue-500 mb-4" />
              <CardTitle>We Got Your Back</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>
                60-day refund guarantee on all purchases. Shop with confidence knowing you're protected.
              </CardDescription>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Sparkles className="h-12 w-12 text-amber-500 mb-4" />
              <CardTitle>$100 in Credits</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Receive $100 in credits every quarter to use on any products in the marketplace.
              </CardDescription>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <Percent className="h-12 w-12 text-emerald-500 mb-4" />
              <CardTitle>10% Off Everything</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Save 10% on every single purchase. The more you buy, the more you save.
              </CardDescription>
            </CardContent>
          </Card>
        </div>

        {/* Pro Features */}
        <Card>
          <CardHeader>
            <CardTitle>Pro Member Benefits</CardTitle>
            <CardDescription>Everything included with your membership</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-emerald-600 text-sm">✓</span>
                </div>
                <div>
                  <p className="font-medium">Early Access</p>
                  <p className="text-sm text-muted-foreground">Be the first to access new products and features</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-emerald-600 text-sm">✓</span>
                </div>
                <div>
                  <p className="font-medium">Priority Support</p>
                  <p className="text-sm text-muted-foreground">Get help faster with dedicated Pro support</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-emerald-600 text-sm">✓</span>
                </div>
                <div>
                  <p className="font-medium">Exclusive Deals</p>
                  <p className="text-sm text-muted-foreground">Access member-only discounts and promotions</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-emerald-600 text-sm">✓</span>
                </div>
                <div>
                  <p className="font-medium">Free Products</p>
                  <p className="text-sm text-muted-foreground">Select from a curated collection of free Pro tools</p>
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>

        {!isProMember && !isFounder && (
          <div className="text-center py-8">
            <Link to="/pricing">
              <Button size="lg" className="px-12">
                View Pricing Plans
              </Button>
            </Link>
          </div>
        )}
      </div>
    </AccountLayout>
  );
};

export default ProMembership;
